import React, { useEffect, useState } from 'react';
import {
  DialogContent, DialogActions, Button, TextField, Stack, FormControl,
  InputLabel, Select, MenuItem, FormControlLabel, Checkbox, Radio, RadioGroup,
  FormLabel, Box, Alert,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import SendIcon   from '@mui/icons-material/Send';
import CancelIcon from '@mui/icons-material/Cancel';

import { AppDispatch, RootState } from '../../reduxStore/Store';
import { sendBroadcast, clearMessages, SendBroadcastPayload } from '../../reduxStore/NotificationSlice';
import SelectUser from '../Selects/SelectUser';
import { User } from '../../models/models';
import { useSnackbar } from '../SnackbarProvider';

interface Props {
  onClose: () => void;
}

type Niveau = 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR';

const BroadcastNotificationForm: React.FC<Props> = ({ onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();
  const { sending, error, successMessage } = useSelector((s: RootState) => s.NotificationSlice);

  const [cible,        setCible]        = useState<'all' | 'user'>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [titre,        setTitre]        = useState('');
  const [message,      setMessage]      = useState('');
  const [niveau,       setNiveau]       = useState<Niveau>('INFO');
  const [popup,        setPopup]        = useState(true);
  const [lien,         setLien]         = useState('');
  const [expiresAt,    setExpiresAt]    = useState('');

  // Feedback : toast sur succès/erreur, puis fermer la dialog
  useEffect(() => {
    if (successMessage) {
      showSnackbar(successMessage, 'success');
      dispatch(clearMessages());
      onClose();
    }
  }, [successMessage, showSnackbar, dispatch, onClose]);

  useEffect(() => {
    if (error) {
      showSnackbar(error, 'error');
      dispatch(clearMessages());
    }
  }, [error, showSnackbar, dispatch]);

  const titreInvalid   = titre.trim().length === 0;
  const messageInvalid = message.trim().length === 0;
  const userInvalid    = cible === 'user' && !selectedUser;
  const formInvalid    = titreInvalid || messageInvalid || userInvalid;

  const handleSubmit = () => {
    if (formInvalid) return;
    const payload: SendBroadcastPayload = {
      target_user_id: cible === 'user' ? selectedUser!.id : null,
      titre:          titre.trim(),
      message:        message.trim(),
      niveau,
      popup,
      lien:           lien.trim() || undefined,
      expires_at:     expiresAt || undefined,
    };
    dispatch(sendBroadcast(payload));
  };

  return (
    <>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 1 }}>

          <FormControl>
            <FormLabel sx={{ mb: 0.5 }}>Destinataire</FormLabel>
            <RadioGroup
              row
              value={cible}
              onChange={(e) => {
                setCible(e.target.value as 'all' | 'user');
                if (e.target.value === 'all') setSelectedUser(null);
              }}
            >
              <FormControlLabel value="all"  control={<Radio />} label="Tous mes utilisateurs" />
              <FormControlLabel value="user" control={<Radio />} label="Un utilisateur spécifique" />
            </RadioGroup>
          </FormControl>

          {cible === 'user' && (
            <SelectUser
              selectedUser={selectedUser}
              onUserChange={setSelectedUser}
              required
            />
          )}

          <TextField
            label="Titre"
            value={titre}
            onChange={(e) => setTitre(e.target.value)}
            error={titreInvalid && titre.length > 0}
            inputProps={{ maxLength: 255 }}
            fullWidth
            required
            size="small"
          />

          <TextField
            label="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            error={messageInvalid && message.length > 0}
            multiline
            minRows={3}
            maxRows={8}
            fullWidth
            required
            size="small"
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Niveau</InputLabel>
              <Select label="Niveau" value={niveau} onChange={(e) => setNiveau(e.target.value as Niveau)}>
                <MenuItem value="INFO">Info</MenuItem>
                <MenuItem value="SUCCESS">Succès</MenuItem>
                <MenuItem value="WARNING">Avertissement</MenuItem>
                <MenuItem value="ERROR">Erreur</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={<Checkbox checked={popup} onChange={(e) => setPopup(e.target.checked)} />}
              label="Toast immédiat"
              sx={{ whiteSpace: 'nowrap' }}
            />
          </Box>

          <TextField
            label="Lien cliquable (optionnel)"
            value={lien}
            onChange={(e) => setLien(e.target.value)}
            placeholder="/articles ou /documentVente/123"
            helperText="URL relative interne — un click sur la notif redirige ici"
            fullWidth
            size="small"
          />

          <TextField
            label="Expire le (optionnel)"
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Si renseigné, la notif disparaîtra automatiquement après cette date"
            fullWidth
            size="small"
          />

          {!popup && (
            <Alert severity="info">
              Mode silencieux : la notification apparaîtra dans le panneau cloche sans pop-up à l'écran.
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} startIcon={<CancelIcon />} disabled={sending}>
          Annuler
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={<SendIcon />}
          disabled={formInvalid || sending}
        >
          {sending ? 'Diffusion...' : 'Diffuser'}
        </Button>
      </DialogActions>
    </>
  );
};

export default BroadcastNotificationForm;
